import { createSelector, createSlice } from '@reduxjs/toolkit'

import {
  fetchClosedDefectsAction,
  fetchDefectsByUser,
  fetchMoreClosedDefectsAction,
  fetchMoreOpenDefectsAction,
  fetchOpenDefectsAction,
} from '../actions/defects/index'
import { AppState, Defect } from '../types'
import { ActionStatus } from '../types/Input'

export type UserDefectsByInstallationGroup = Record<string, Record<'open' | 'closed', Defect[]>>

export interface DefectsState {
  openDefects: Defect[]
  closedDefects: Defect[]
  userDefects: UserDefectsByInstallationGroup
  openStatus: ActionStatus
  closedStatus: ActionStatus
  userStatus: ActionStatus
  openError?: string
  closedError?: string
  userError?: string
  openToken?: string
  closedToken?: string
  userToken?: string
  moreOpenStatus: ActionStatus
  moreClosedStatus: ActionStatus
  moreOpenError?: string
  moreClosedError?: string

  byVehicle: Record<
    string,
    {
      open: Defect[]
      closed: Defect[]
      loadingClosed: boolean
      fetchedClosed: boolean
    }
  >
  loadingAllOpen: boolean
  fetchedAllOpen: boolean
}

const initialState: DefectsState = {
  openDefects: [],
  closedDefects: [],
  userDefects: {},
  openStatus: 'none',
  closedStatus: 'none',
  userStatus: 'none',
  moreOpenStatus: 'none',
  moreClosedStatus: 'none',

  byVehicle: {},
  loadingAllOpen: false,
  fetchedAllOpen: false,
}

export const defectsSlice = createSlice({
  name: 'defect',
  initialState,
  reducers: {
    resetDefects: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchOpenDefectsAction.pending, (state) => {
      state.openStatus = 'loading'
      state.openError = 'undefined'
      state.loadingAllOpen = true
      state.fetchedAllOpen = false
    })

    builder.addCase(fetchOpenDefectsAction.fulfilled, (state, { payload, meta }) => {
      state.openDefects = payload.defects
      state.openToken = payload.token
      state.openStatus = 'succeeded'

      const installationGroups = meta.arg?.installationGroups
      if (installationGroups) {
        const groups = installationGroups.split(',')
        groups.forEach((group) => {
          if (!state.byVehicle[group]) {
            state.byVehicle[group] = {
              open: [],
              closed: [],
              loadingClosed: false,
              fetchedClosed: false,
            }
          }
          state.byVehicle[group].open = payload.defects.filter((d) => d.installationGroup === group)
        })
      }
      state.loadingAllOpen = false
      state.fetchedAllOpen = true
    })

    builder.addCase(fetchOpenDefectsAction.rejected, (state, { payload }) => {
      if (payload) {
        state.openError = payload.message
      }
      state.openStatus = 'failed'
      state.loadingAllOpen = false
      state.fetchedAllOpen = false
    })

    builder.addCase(fetchMoreOpenDefectsAction.pending, (state) => {
      state.moreOpenStatus = 'loading'
      state.moreOpenError = 'undefined'
    })

    builder.addCase(fetchMoreOpenDefectsAction.fulfilled, (state, { payload }) => {
      state.openDefects = [...state.openDefects, ...payload.defects]
      state.openToken = payload.token
      state.moreOpenStatus = 'succeeded'
    })

    builder.addCase(fetchMoreOpenDefectsAction.rejected, (state, { payload }) => {
      if (payload) {
        state.moreOpenError = payload.message
      }
      state.moreOpenStatus = 'failed'
    })

    builder.addCase(fetchClosedDefectsAction.pending, (state, { meta }) => {
      state.closedStatus = 'loading'
      state.closedError = 'undefined'

      const vehicleId = meta.arg?.installationGroups
      if (vehicleId) {
        if (!state.byVehicle[vehicleId]) {
          state.byVehicle[vehicleId] = {
            open: [],
            closed: [],
            loadingClosed: false,
            fetchedClosed: false,
          }
        }
        state.byVehicle[vehicleId].loadingClosed = true
      }
    })

    builder.addCase(fetchClosedDefectsAction.fulfilled, (state, { payload, meta }) => {
      state.closedDefects = payload.defects
      state.closedToken = payload.token
      state.closedStatus = 'succeeded'

      const vehicleId = meta.arg?.installationGroups
      if (vehicleId) {
        state.byVehicle[vehicleId].closed = payload.defects
        state.byVehicle[vehicleId].loadingClosed = false
        state.byVehicle[vehicleId].fetchedClosed = true
      }
    })

    builder.addCase(fetchClosedDefectsAction.rejected, (state, { payload, meta }) => {
      if (payload) {
        state.closedError = payload.message
      }
      state.closedStatus = 'failed'

      const vehicleId = meta.arg?.installationGroups
      if (vehicleId && state.byVehicle[vehicleId]) {
        state.byVehicle[vehicleId].loadingClosed = false
      }
    })

    builder.addCase(fetchMoreClosedDefectsAction.pending, (state) => {
      state.moreClosedStatus = 'loading'
      state.moreClosedError = 'undefined'
    })

    builder.addCase(fetchMoreClosedDefectsAction.fulfilled, (state, { payload }) => {
      state.closedDefects = [...state.closedDefects, ...payload.defects]
      state.closedToken = payload.token
      state.moreClosedStatus = 'succeeded'
    })

    builder.addCase(fetchMoreClosedDefectsAction.rejected, (state, { payload }) => {
      if (payload) {
        state.moreClosedError = payload.message
      }
      state.moreClosedStatus = 'failed'
    })

    builder.addCase(fetchDefectsByUser.pending, (state) => {
      state.userStatus = 'loading'
      state.userError = undefined
    })

    builder.addCase(fetchDefectsByUser.fulfilled, (state, { payload }) => {
      state.userDefects = payload.defects
      state.userToken = payload.token
      state.userStatus = 'succeeded'
    })

    builder.addCase(fetchDefectsByUser.rejected, (state, { payload }) => {
      if (payload) {
        state.userError = payload.message
      }
      state.userStatus = 'failed'
    })
  },
})

// criticality orders are not linear
const criticalityOrder = '1243'
export const defectComparator = (a: Defect, b: Defect) =>
  // more critical defect first
  criticalityOrder.indexOf(a.criticality) - criticalityOrder.indexOf(b.criticality) ||
  // defect with restrictions first
  Number(Boolean(b.restrictionCodeDescription)) - Number(Boolean(a.restrictionCodeDescription)) ||
  // more recent defect first
  new Date(b.issuedTime).getTime() - new Date(a.issuedTime).getTime()

export const openDefectsSorted = createSelector(
  (state: AppState): Defect[] => state.defects.openDefects,
  (defects: Defect[]): Defect[] => defects.slice().sort(defectComparator)
)

export const selectUserDefectsGrouped = createSelector(
  (state: AppState): UserDefectsByInstallationGroup => state.defects.userDefects,
  (defectsByGroup: UserDefectsByInstallationGroup): UserDefectsByInstallationGroup => {
    return defectsByGroup
  }
)

export const selectUserDefectsCount = createSelector(selectUserDefectsGrouped, (grouped) =>
  Object.values(grouped).reduce(
    (count, defects) => count + defects.open.length + defects.closed.length,
    0
  )
)

export const { resetDefects } = defectsSlice.actions

export default defectsSlice.reducer
